import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import type { UserRole } from '@/types';
import { LoadingSkeleton } from '@/components/ui';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** If specified, the user must have at least one of these roles */
  requiredRoles?: UserRole[];
  /** Where to redirect if not authenticated (default: /login) */
  redirectTo?: string;
}

/**
 * Wraps routes that require authentication (and optionally specific roles).
 * Shows a loading skeleton while auth state is initializing.
 * Redirects to /login if not authenticated, or /unauthorized if wrong role.
 */
export function ProtectedRoute({
  children,
  requiredRoles,
  redirectTo = '/login',
}: ProtectedRouteProps) {
  const { initialized, user } = useAuthStore();
  const location = useLocation();

  // Still loading auth state — show skeleton
  if (!initialized) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[var(--color-bg-primary)]">
        <div className="w-full max-w-md p-8 space-y-4">
          <LoadingSkeleton className="h-8 w-48 mx-auto" />
          <LoadingSkeleton className="h-4 w-full" />
          <LoadingSkeleton className="h-4 w-3/4" />
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!user) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check email verification (skip for superAdmin)
  if (!user.emailVerified && !user.roles.includes('superAdmin')) {
    return <Navigate to="/verify-email" state={{ from: location }} replace />;
  }

  // Role check
  if (requiredRoles && requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some((r) => user.roles.includes(r));
    if (!hasRequiredRole) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
}

/**
 * Redirect already-authenticated users away from auth pages (login, signup).
 */
export function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { initialized, user } = useAuthStore();
  const location = useLocation();

  if (!initialized) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[var(--color-bg-primary)]">
        <LoadingSkeleton className="h-8 w-32" />
      </div>
    );
  }

  if (user) {
    const from = (location.state as { from?: Location })?.from?.pathname ?? '/dashboard';
    return <Navigate to={from} replace />;
  }

  return <>{children}</>;
}
